import React, { useState, useEffect } from 'react';
import { formatDateLong } from '../utils/calendarConstants';
import { getTimeDisplay, getTimeRangeTooltip } from '../utils/calendarUtils';

function CalendarPopover({ popover, entries, draftEntry, onDraftChange, onAddEntry, onToggleEntry, onDeleteEntry, onUpdateEntry, onClose, popoverRef, viewMode }) {
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [hasTimeSet, setHasTimeSet] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editText, setEditText] = useState('');
  const [editStartTime, setEditStartTime] = useState(null);
  const [editEndTime, setEditEndTime] = useState(null);

  useEffect(() => {
    if (popover && viewMode === 'week' && !editingEntry) {
      // If clickedHour is provided, use that instead of current time
      if (popover.clickedHour !== null && popover.clickedHour !== undefined) {
        const clickedHour = popover.clickedHour;
        const endHour = (clickedHour + 1) % 24;
        setStartTime(`${String(clickedHour).padStart(2, '0')}:00`);
        setEndTime(`${String(endHour).padStart(2, '0')}:00`);
        setHasTimeSet(false); // Gray icon, user can change it
      } else {
        // Use current time as before
        const now = new Date();
        const minutes = now.getMinutes();
        const roundedMinutes = Math.round(minutes / 15) * 15;
        const roundedTime = new Date(now);
        roundedTime.setMinutes(roundedMinutes, 0, 0);
        
        const startHour = roundedTime.getHours();
        const startMin = roundedTime.getMinutes();
        const endHour = (startHour + 1) % 24;
        
        setStartTime(`${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`);
        setEndTime(`${String(endHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`);
        setHasTimeSet(false);
      }
    } else if (!popover || viewMode !== 'week') {
      setStartTime(null);
      setEndTime(null);
      setHasTimeSet(false);
    }
  }, [popover, viewMode, editingEntry]);

  if (!popover) return null;

  const activeEntries = entries[popover.date] || [];

  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let min = 0; min < 60; min += 15) {
        const timeStr = `${String(hour).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
        options.push(timeStr);
      }
    }
    return options;
  };

  const timeOptions = generateTimeOptions();

  const handleTimeIconClick = (e) => {
    e.stopPropagation();
    setShowTimePicker(!showTimePicker);
  };

  const handleTimeChange = (newStartTime, newEndTime) => {
    setStartTime(newStartTime);
    setEndTime(newEndTime);
    if (newStartTime && newEndTime) {
      setHasTimeSet(true);
    } else {
      setHasTimeSet(false);
    }
  };

  const handleTimePickerClose = () => {
    setShowTimePicker(false);
  };

  const handleAddWithTime = () => {
    if (viewMode === 'week' && hasTimeSet && startTime && endTime) {
      onAddEntry(startTime, endTime);
    } else {
      onAddEntry();
    }
    // Reset time state after adding
    setHasTimeSet(false);
    setShowTimePicker(false);
  };

  const handleEditClick = (entry) => {
    setEditingEntry(entry.id);
    setEditText(entry.text);
    setEditStartTime(entry.startTime || null);
    setEditEndTime(entry.endTime || null);
  };

  const handleSaveEdit = () => {
    if (editingEntry && editText.trim()) {
      onUpdateEntry(popover.date, editingEntry, editText, editStartTime, editEndTime);
      setEditingEntry(null);
      setEditText('');
      setEditStartTime(null);
      setEditEndTime(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingEntry(null);
    setEditText('');
    setEditStartTime(null);
    setEditEndTime(null);
  };

  return (
    <div
      className="calendar-popover"
      ref={popoverRef}
      style={{ top: popover.top, left: popover.left }}
      onClick={(event) => event.stopPropagation()}
    >
      <div className="popover-header">
        <div>
          <p className="popover-kicker">Entries for</p>
          <h3>{formatDateLong(popover.date)}</h3>
        </div>
        <button
          className="popover-close"
          onClick={onClose}
        >
          ×
        </button>
      </div>

      <div className="popover-entries">
        {activeEntries.length === 0 && (
          <p className="empty-state">
            Nothing planned yet — add a pocket below.
          </p>
        )}
        {activeEntries.map((entry) => {
          const timeDisplay = getTimeDisplay(entry);
          const timeTooltip = getTimeRangeTooltip(entry);
          const isEditing = editingEntry === entry.id;
          
          if (isEditing) {
            return (
              <div key={entry.id} className="popover-entry editing">
                <div className="edit-mode">
                  <input
                    type="text"
                    value={editText}
                    onChange={(e) => setEditText(e.target.value)}
                    className="edit-text-input"
                    autoFocus
                  />
                  {viewMode === 'week' && (
                    <div className="edit-time-picker">
                      <select
                        value={editStartTime || ''}
                        onChange={(e) => setEditStartTime(e.target.value)}
                        className="edit-time-select"
                      >
                        <option value="">All day</option>
                        {timeOptions.map((time) => (
                          <option key={time} value={time}>
                            {time}
                          </option>
                        ))}
                      </select>
                      {editStartTime && (
                        <select
                          value={editEndTime || ''}
                          onChange={(e) => setEditEndTime(e.target.value)}
                          className="edit-time-select"
                        >
                          {timeOptions.map((time) => (
                            <option key={time} value={time}>
                              {time}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  )}
                  <div className="edit-actions">
                    <button className="edit-save" onClick={handleSaveEdit}>
                      Save
                    </button>
                    <button className="edit-cancel" onClick={handleCancelEdit}>
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            );
          }
          
          return (
            <div
              key={entry.id}
              className={`popover-entry ${entry.done ? 'done' : ''}`}
              title={timeTooltip}
            >
              <div>
                <button
                  className="entry-toggle"
                  onClick={() => onToggleEntry(popover.date, entry.id)}
                >
                  {entry.done ? '✔' : ''}
                </button>
                <span className="entry-text">{entry.text}</span>
                {viewMode === 'week' && timeDisplay && (
                  <span className="entry-time">{timeDisplay}</span>
                )}
              </div>
              <div className="entry-actions">
                <button
                  className="entry-edit"
                  onClick={() => handleEditClick(entry)}
                >
                  Edit
                </button>
                <button
                  className="entry-delete"
                  onClick={() => onDeleteEntry(popover.date, entry.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="popover-add">
        <input
          type="text"
          placeholder="Add pocket..."
          value={draftEntry}
          onChange={(event) => onDraftChange(event.target.value)}
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              handleAddWithTime();
            }
          }}
        />
        {viewMode === 'week' && (
          <button
            className={`time-icon-button ${hasTimeSet ? 'has-time' : ''}`}
            onClick={handleTimeIconClick}
            type="button"
            title="Set time"
          >
            🕐
          </button>
        )}
        <button onClick={handleAddWithTime}>Add</button>
        {showTimePicker && viewMode === 'week' && (
          <div className="time-picker-popup" onClick={(e) => e.stopPropagation()}>
            <div className="time-picker-header">
              <span>Set Time</span>
              <button
                className="time-picker-close"
                onClick={handleTimePickerClose}
              >
                ×
              </button>
            </div>
            <div className="time-picker-content">
              <div className="time-selector">
                <label>Start:</label>
                <select
                  value={startTime || ''}
                  onChange={(e) => handleTimeChange(e.target.value, endTime)}
                >
                  {timeOptions.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
              <div className="time-selector">
                <label>End:</label>
                <select
                  value={endTime || ''}
                  onChange={(e) => handleTimeChange(startTime, e.target.value)}
                >
                  {timeOptions.map((time) => (
                    <option key={time} value={time}>
                      {time}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default CalendarPopover;

